/**
 * Location Zustand Store
 * 
 * Provides centralized location state management for TabPicker geolocation features.
 * Handles user location preferences, current location data, and template discovery state.
 * 
 * Features:
 * - TypeScript-first with strict typing
 * - Persistent location preferences
 * - Privacy-focused location handling
 * - Integration with location services
 * - Template discovery state management
 * 
 * @module TabPicker.LocationStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LocationService, type FuzzedLocation, type LocationContext, type LocationContextType } from '../utils/locationServices';

/**
 * User Location Preferences
 * 
 * Represents user preferences for location sharing and discovery.
 */
export interface LocationPreferences {
  /** Whether location sharing is enabled for this user */
  locationSharingEnabled: boolean;
  /** Maximum discovery radius in meters (default: 50km) */
  maxDiscoveryRadius: number;
  /** Default fuzzing radius for privacy protection */
  fuzzingRadius: number;
  /** Whether to auto-share location on game creation */
  autoShareOnCreation: boolean;
}

/**
 * Current User Location Data
 * 
 * Represents the user's current location with privacy protections.
 */
export interface UserLocation {
  /** Original coordinates from browser geolocation */
  coordinates: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  /** Fuzzed coordinates for privacy */
  fuzzedLocation: FuzzedLocation;
  /** Timestamp when location was obtained */
  timestamp: number;
  /** Context in which location was requested */
  context: LocationContextType;
}

/**
 * Location Store State
 * 
 * Defines the complete state shape for location management.
 */
interface LocationState {
  /** User location preferences */
  preferences: LocationPreferences;
  /** Current user location (expires after 5 minutes) */
  currentLocation: UserLocation | null;
  /** Whether location permission has been requested/granted */
  locationPermissionGranted: boolean;
  /** Whether location permission was explicitly denied */
  locationPermissionDenied: boolean;
  /** Whether location request is in progress */
  requestingLocation: boolean;
  /** Last context used for location request */
  lastLocationContext: LocationContextType | null;
  /** Whether location features are initialized */
  initialized: boolean;
}

/**
 * Location Store Actions
 * 
 * Defines all actions available for location state management.
 */
interface LocationActions {
  /** Initialize location store from persistence */
  initialize: () => void;
  /** Update user location preferences */
  updatePreferences: (preferences: Partial<LocationPreferences>) => void;
  /** Request user location with context */
  requestLocation: (context: LocationContext, contextType: LocationContextType) => Promise<UserLocation | null>;
  /** Set current user location */
  setCurrentLocation: (location: UserLocation | null) => void;
  /** Clear current location */
  clearCurrentLocation: () => void;
  /** Mark location permission as granted */
  setLocationPermissionGranted: (granted: boolean) => void;
  /** Mark location permission as denied */
  setLocationPermissionDenied: (denied: boolean) => void;
  /** Clear all location data (privacy control) */
  clearAllLocationData: () => void;
  /** Reset location permission states */
  resetPermissionStates: () => void;
  /** Check if current location is still valid (not expired) */
  isLocationValid: () => boolean;
  /** Get fuzzed coordinates for API calls */
  getFuzzedCoordinates: () => { latitude: number; longitude: number } | null;
  /** Reset entire store */
  reset: () => void;
}

/**
 * Complete Location Store Type
 */
export type LocationStore = LocationState & LocationActions;

/**
 * Default location preferences
 */
const defaultPreferences: LocationPreferences = {
  locationSharingEnabled: true,
  maxDiscoveryRadius: 50000, // 50km
  fuzzingRadius: 1000, // 1km
  autoShareOnCreation: false,
};

/**
 * Initial store state
 */
const initialState: LocationState = {
  preferences: defaultPreferences,
  currentLocation: null,
  locationPermissionGranted: false,
  locationPermissionDenied: false,
  requestingLocation: false,
  lastLocationContext: null,
  initialized: false,
};

/**
 * Location store implementation
 */
export const useLocationStore = create<LocationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Actions
      initialize: () => {
        set({ initialized: true });
        
        // Check for expired location
        const state = get();
        if (state.currentLocation && !get().isLocationValid()) {
          get().clearCurrentLocation();
        }
      },

      updatePreferences: (newPreferences) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...newPreferences,
          },
        }));
      },

      requestLocation: async (context, contextType) => {
        set({ 
          requestingLocation: true, 
          lastLocationContext: contextType 
        });

        try {
          const position = await LocationService.requestLocation(context);
          
          if (position) {
            const { preferences } = get();
            
            // Create user location with fuzzing
            const fuzzedLocation = LocationService.fuzzLocation(
              {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
              },
              preferences.fuzzingRadius
            );

            const userLocation: UserLocation = {
              coordinates: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
              },
              fuzzedLocation,
              timestamp: Date.now(),
              context: contextType,
            };

            set({
              currentLocation: userLocation,
              locationPermissionGranted: true,
              locationPermissionDenied: false,
              requestingLocation: false,
            });

            return userLocation;
          } else {
            set({
              locationPermissionDenied: true,
              locationPermissionGranted: false,
              requestingLocation: false,
            });
            return null;
          }
        } catch (error) {
          set({
            locationPermissionDenied: true,
            locationPermissionGranted: false,
            requestingLocation: false,
          });
          return null;
        }
      },

      setCurrentLocation: (location) => {
        set({ currentLocation: location });
      },

      clearCurrentLocation: () => {
        set({ currentLocation: null });
      },

      setLocationPermissionGranted: (granted) => {
        set({ 
          locationPermissionGranted: granted,
          locationPermissionDenied: !granted,
        });
      },

      setLocationPermissionDenied: (denied) => {
        set({ 
          locationPermissionDenied: denied,
          locationPermissionGranted: !denied,
        });
      },

      clearAllLocationData: () => {
        set({
          currentLocation: null,
          locationPermissionGranted: false,
          locationPermissionDenied: false,
          lastLocationContext: null,
        });
        
        // Clear browser storage as well
        LocationService.clearLocationPreferences();
      },

      resetPermissionStates: () => {
        set({
          locationPermissionGranted: false,
          locationPermissionDenied: false,
          lastLocationContext: null,
        });
      },

      isLocationValid: () => {
        const { currentLocation } = get();
        if (!currentLocation) return false;
        
        // Location expires after 5 minutes
        const expiryTime = 5 * 60 * 1000;
        return Date.now() - currentLocation.timestamp < expiryTime;
      },

      getFuzzedCoordinates: () => {
        const { currentLocation } = get();
        if (!currentLocation || !get().isLocationValid()) {
          return null;
        }
        
        return {
          latitude: currentLocation.fuzzedLocation.latitude,
          longitude: currentLocation.fuzzedLocation.longitude,
        };
      },

      reset: () => {
        set(initialState);
        LocationService.clearLocationPreferences();
      },
    }),
    {
      name: 'tabpicker-location-store',
      // Persist preferences, permission states, and current location (with built-in expiration)
      partialize: (state) => ({
        preferences: state.preferences,
        currentLocation: state.currentLocation, // Safe to persist - has 5-minute expiration
        locationPermissionGranted: state.locationPermissionGranted,
        locationPermissionDenied: state.locationPermissionDenied,
        lastLocationContext: state.lastLocationContext,
        initialized: state.initialized,
      }),
    }
  )
);

/**
 * Location Store Selectors
 * 
 * Convenient selectors for accessing store state.
 */
export const locationSelectors = {
  preferences: (state: LocationStore) => state.preferences,
  currentLocation: (state: LocationStore) => state.currentLocation,
  isLocationAvailable: (state: LocationStore) => state.currentLocation !== null && state.isLocationValid(),
  isLocationSharingEnabled: (state: LocationStore) => state.preferences.locationSharingEnabled,
  hasLocationPermission: (state: LocationStore) => state.locationPermissionGranted && !state.locationPermissionDenied,
  isRequestingLocation: (state: LocationStore) => state.requestingLocation,
  maxDiscoveryRadius: (state: LocationStore) => state.preferences.maxDiscoveryRadius,
  fuzzingRadius: (state: LocationStore) => state.preferences.fuzzingRadius,
};

/**
 * Custom hooks for location store
 */

export const useLocationPreferences = () => useLocationStore(locationSelectors.preferences);
export const useCurrentLocation = () => useLocationStore(locationSelectors.currentLocation);
export const useIsLocationAvailable = () => useLocationStore(locationSelectors.isLocationAvailable);
export const useLocationSharingEnabled = () => useLocationStore(locationSelectors.isLocationSharingEnabled);
export const useHasLocationPermission = () => useLocationStore(locationSelectors.hasLocationPermission);
export const useIsRequestingLocation = () => useLocationStore(locationSelectors.isRequestingLocation);
export const useMaxDiscoveryRadius = () => useLocationStore(locationSelectors.maxDiscoveryRadius);

/**
 * Location actions hook
 */
export const useLocationActions = () => useLocationStore((state) => ({
  initialize: state.initialize,
  updatePreferences: state.updatePreferences,
  requestLocation: state.requestLocation,
  setCurrentLocation: state.setCurrentLocation,
  clearCurrentLocation: state.clearCurrentLocation,
  setLocationPermissionGranted: state.setLocationPermissionGranted,
  setLocationPermissionDenied: state.setLocationPermissionDenied,
  clearAllLocationData: state.clearAllLocationData,
  resetPermissionStates: state.resetPermissionStates,
  isLocationValid: state.isLocationValid,
  getFuzzedCoordinates: state.getFuzzedCoordinates,
  reset: state.reset,
}));